import React, { MouseEvent } from 'react'
import { css } from '@emotion/core'
import { PreparationType, RoomStatusType, RoomType, ReservationType } from '@/models/room-manager/response/guest-room-indicator'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'

// リピーターアイコンの表示条件を判定
const shouldShowRepeaterIcon = (reservation: ReservationType | undefined): boolean => {
  if (!reservation) return false

  const stayCount = reservation.stayCount
  const roomAssignStatus = reservation.roomAssignStatus

  // stayCountが存在しない、または0の場合は表示しない
  if (!stayCount || stayCount < 1) return false

  // 予約・CI：宿泊回数が1回以上
  if (roomAssignStatus === ApprovedStatusType.Reservation || roomAssignStatus === ApprovedStatusType.Checkin) {
    return stayCount >= 1
  }
  // 滞在中・CO：宿泊回数が2回以上
  if (roomAssignStatus === ApprovedStatusType.Stay || roomAssignStatus === ApprovedStatusType.Checkout) {
    return stayCount >= 2
  }
  return false
}

const statusClassNameMap: { [k in RoomStatusType]: string } = {
  [RoomStatusType.empty]: '-empty',
  [RoomStatusType.checkIn]: '-check-in',
  [RoomStatusType.stay]: '-stay',
  [RoomStatusType.checkOut]: '-check-out',
  [RoomStatusType.cleaning]: '-cleaning',
}

const preparationTypeLabel = (type: PreparationType | null): string => {
  if (type === null) return '-'
  return type === PreparationType.OutOfOrder ? 'OOO' : 'OOS'
}

const settleWhiteIcon = require('@/static/images/room/settle_white.svg')
const settleGrayIcon = require('@/static/images/room/settle_gray.svg')

type Props = {
  reservation: ReservationType | undefined
  room: RoomType
  isSelected: boolean
  isDisable: boolean
  onClick: (event: MouseEvent<HTMLDivElement>) => void
  boxZoomRate: number
  isHighlighted: boolean
  onMouseEnter: (reservation: ReservationType | undefined) => void
  onMouseLeave: () => void
}

export const IndicatorRoom: React.FC<Props> = ({
  reservation,
  room,
  isSelected,
  onClick,
  isDisable,
  boxZoomRate,
  isHighlighted,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { i18n } = useTranslation()

  const roomTypeName = room.roomShortName || (i18n.language === 'ja' ? room.roomName : room.roomNameEn)
  // currentReservation(=Stay状態の予約)がある間は宿泊予約情報を優先し、準備中カードを隠す
  const shouldShowPreparation = !!room.preparation && !room.currentReservation
  const statusClassName = shouldShowPreparation ? '-preparation' : statusClassNameMap[room.roomStatus]
  const nameText = shouldShowPreparation ? room.preparation!.preparationMemo || t('In preparation') : reservation?.guestName || '-'
  const nightsText = shouldShowPreparation
    ? preparationTypeLabel(room.preparation!.preparationType)
    : reservation
      ? `${reservation.currentNights}/${reservation.totalNights}`
      : '-'

  return (
    <div css={[boxStyle, { zoom: boxZoomRate }]}>
      <div
        css={[roomStyle, isHighlighted && highlightActiveStyle]}
        className={`${statusClassName} ${isSelected ? '-selected' : ''}`}
        onClick={onClick}
        onMouseEnter={() => onMouseEnter(reservation)}
        onMouseLeave={onMouseLeave}
      >
        <div className="room-header" />
        <div className="room-body">
          <div className="room-row-1">
            <p>{room.roomNumber}</p>
            <p className="small-text">{roomTypeName}</p>
          </div>
          <div className="room-row-2">
            <p>{nameText}</p>
          </div>
          <div className="room-row-3">
            <p className="small-text">{nightsText}</p>
            {!shouldShowPreparation && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {shouldShowRepeaterIcon(reservation) && (
                  <img src={require('@/static/images/repeater-icon.svg')} width={14} height={14} alt="repeater" />
                )}
                {reservation?.hasUnpaidSales && <img src={isSelected ? settleWhiteIcon : settleGrayIcon} width={14} alt="settle" />}
              </div>
            )}
          </div>
        </div>
      </div>
      {isDisable && <div css={disableStyle} />}
    </div>
  )
}

const boxStyle = css({
  margin: 1,
  position: 'relative',
  minWidth: 83 - 2,
  width: 83 - 2,
  minHeight: 60 - 2,
  height: 60 - 2,
})

const highlightActiveStyle = css({
  boxShadow: `
    0 0 0 2px #1890ff,
    0 0 8px 0 rgba(24, 144, 255, 0.4),
    0 0 16px 0 rgba(24, 144, 255, 0.2)
  `,
  borderRadius: 4,
  zIndex: 10,
  transform: 'scale(1.02)',
  transition: 'all 0.2s ease-in-out',
})

const roomStatusColor = css({
  '&.-selected': {
    '*': { color: '#FFFFFF !important' },
  },
  '&.-empty': {
    backgroundColor: '#FFFFFF',
    '.room-header': { backgroundColor: '#676767' },
    '.room-row-2 p': { color: '#676767' },
    '&.-selected': { backgroundColor: '#676767' },
  },
  '&.-check-in': {
    backgroundColor: '#FEF3F0',
    '.room-header': { backgroundColor: '#F47110' },
    '.room-row-2 p': { color: '#F47110' },
    '&.-selected': { backgroundColor: '#F47110' },
  },
  '&.-stay': {
    backgroundColor: '#EFF9FF',
    '.room-header': { backgroundColor: '#3E85CC' },
    '.room-row-2 p': { color: '#3E85CC' },
    '&.-selected': { backgroundColor: '#3E85CC' },
  },
  '&.-check-out': {
    backgroundColor: '#F6FDF2',
    '.room-header': { backgroundColor: '#7DC931' },
    '.room-row-2 p': { color: '#7DC931' },
    '&.-selected': { backgroundColor: '#7DC931' },
  },
  '&.-cleaning': {
    backgroundColor: '#FFFCDD',
    '.room-header': { backgroundColor: '#F2A40B' },
    '.room-row-2 p': { color: '#F2A40B' },
    '&.-selected': { backgroundColor: '#F2A40B' },
  },
  '&.-preparation': {
    backgroundColor: '#F0F0F0',
    '.room-header': { backgroundColor: '#A3A3A3' },
    '.room-row-2 p': { color: '#676767' },
    '&.-selected': { backgroundColor: '#A3A3A3' },
  },
})

const roomStyle = css(roomStatusColor, {
  height: '100%',
  width: '100%',
  position: 'absolute',
  cursor: 'pointer',
  '.room-header': {
    height: 3,
  },
  '.room-body': {
    padding: 5,
    display: 'flex',
    flexFlow: 'column',
    height: 'calc(100% - 2px)',
    justifyContent: 'space-between',
  },
  '.room-row-1': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: 37,
    },
  },
  '.room-row-2': {
    lineHeight: '100%',
    p: {
      whiteSpace: 'nowrap',
      textOverflow: 'clip',
      overflow: 'hidden',
      fontSize: 11,
    },
  },
  '.room-row-3': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  p: {
    color: '#A3A3A3',
    fontWeight: 'bold',
    fontSize: 10,
    letterSpacing: 0.6,
    '&.small-text': {
      fontSize: 9,
      letterSpacing: 0.45,
    },
  },
})

const disableStyle = css({
  width: 'calc(100% + 2px)',
  height: 'calc(100% + 2px)',
  background: '#000000',
  opacity: 0.3,
  position: 'absolute',
})
